import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Badge,
  Box,
} from "@mui/material";
import BeachAccessIcon from "@mui/icons-material/BeachAccess";
import WarningIcon from "@mui/icons-material/WarningAmber";
import MenuIcon from "@mui/icons-material/Menu";
import AccountCircle from "@mui/icons-material/AccountCircle";

import { signOut } from "../features/authSlice";
import { auth } from "../firebaseConfig";
import { SIGNUPS_ENABLED } from "../config";

import AuthDialog from "./AuthDialog";
import UpdateInfo from "./UpdateInfo";
import AllCartsDialog from "./AllCartsDialog";
import PaidSummaryDialog from "./PaidSummaryDialog";
import PurgeInfoDialog from "./PurgeInfoDialog";
import SpotPicker from "./SpotPicker";

const AppToolbar = () => {
  const dispatch = useDispatch();
  const { user, loading } = useSelector((state) => state.auth);
  const [anchorEl, setAnchorEl] = useState(null);
  const [authDialogOpen, setAuthDialogOpen] = useState(false);
  const [authMode, setAuthMode] = useState("signIn");
  const [updateInfo, setUpdateInfo] = useState(false);
  const [anchorElMenu, setAnchorElMenu] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [purgeOpen, setPurgeOpen] = useState(false);
  const [spotMapOpen, setSpotMapOpen] = useState(false);

  const handleMenuOpen = (event) => setAnchorElMenu(event.currentTarget);
  const handleMenuClose = () => setAnchorElMenu(null);
  const handleOpenDialog = () => {
    setDialogOpen(true);
    handleMenuClose();
  };
  const handleOpenSummary = () => {
    setSummaryOpen(true);
    handleMenuClose();
  };
  const handleOpenPurge = () => {
    setPurgeOpen(true);
    handleMenuClose();
  };
  const handleOpenSpotMap = () => {
    setSpotMapOpen(true);
    handleMenuClose();
  };

  const handleClick = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleSignOut = () => {
    dispatch(signOut());
    handleClose();
  };

  const handleAuthClick = (mode) => {
    setAuthMode(mode);
    setAuthDialogOpen(true);
  };

  const handleAuthClose = () => {
    setAuthDialogOpen(false);
    handleClose();
  };

  const openUpdateInfo = () => setUpdateInfo(true);
  const closeUpdateInfo = () => {
    setUpdateInfo(false);
    handleClose();
  };

  React.useEffect(() => {
    setUpdateInfo(Boolean(!loading && user?.uid && !user.building));
  }, [user, loading]);

  React.useEffect(() => {
    auth.currentUser?.getIdTokenResult().then((idTokenResult) => {
      setIsAdmin(Boolean(idTokenResult.claims?.isAdmin));
    });
  }, [user]);

  return (
    <AppBar position="sticky">
      <Toolbar>
        {isAdmin && (
          <>
            <IconButton edge="start" color="inherit" onClick={handleMenuOpen}>
              <MenuIcon />
            </IconButton>
            <Menu
              anchorEl={anchorElMenu}
              open={Boolean(anchorElMenu)}
              onClose={handleMenuClose}
            >
              <MenuItem onClick={handleOpenDialog}>View All Carts</MenuItem>
              <MenuItem onClick={handleOpenSummary}>
                Summarize Paid Confirmations
              </MenuItem>
              <MenuItem onClick={handleOpenSpotMap}>Beach Spot Map</MenuItem>
              <MenuItem onClick={handleOpenPurge} sx={{ color: "error.main" }}>
                Purge Unit & Building Info
              </MenuItem>
            </Menu>
          </>
        )}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexGrow: 1 }}>
          <BeachAccessIcon />
          <Typography variant="h6">Destin Chairs</Typography>
        </Box>
        {user && !user.building ? (
          <Badge badgeContent={<WarningIcon sx={{ color: "warning.main" }} />}>
            <IconButton onClick={handleClick} color="inherit">
              <AccountCircle />
            </IconButton>
          </Badge>
        ) : (
          <IconButton onClick={handleClick} color="inherit">
            <AccountCircle />
          </IconButton>
        )}
        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
          {user && <MenuItem disabled>{user.email}</MenuItem>}
          {user && (
            <MenuItem
              onClick={openUpdateInfo}
              sx={{
                color: (t) =>
                  user.building ? t.palette.text.primary : t.palette.warning.main,
              }}
            >
              Update Info
            </MenuItem>
          )}
          {user && <MenuItem onClick={handleSignOut}>Sign Out</MenuItem>}
          {!user && (
            <MenuItem onClick={() => handleAuthClick("signIn")}>Sign In</MenuItem>
          )}
          {!user && SIGNUPS_ENABLED && (
            <MenuItem onClick={() => handleAuthClick("signUp")}>Sign Up</MenuItem>
          )}
        </Menu>
      </Toolbar>
      <AllCartsDialog open={dialogOpen} onClose={() => setDialogOpen(false)} />
      <PaidSummaryDialog open={summaryOpen} onClose={() => setSummaryOpen(false)} />
      <PurgeInfoDialog open={purgeOpen} onClose={() => setPurgeOpen(false)} />
      <SpotPicker open={spotMapOpen} onClose={() => setSpotMapOpen(false)} />
      <AuthDialog open={authDialogOpen} onClose={handleAuthClose} mode={authMode} />
      <UpdateInfo open={updateInfo} onClose={closeUpdateInfo} />
    </AppBar>
  );
};

export default AppToolbar;
